import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Typography,
  Collapse,
  IconButton,
  Switch,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faChevronDown,
  faChevronUp,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Green circular progress with a centered "N%" label. Idle/settled state is
// 100% (results are fully filtered); while a filter pass is computing it
// steps up in 10% increments and snaps to 100% the moment it finishes.
const SearchProgressCircle = ({ value }) => (
  <Box sx={{ position: "relative", display: "inline-flex" }}>
    <CircularProgress
      variant="determinate"
      value={value}
      size={56}
      thickness={5}
      sx={{ color: "success.main" }}
    />
    <Box
      sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={{ color: "success.main", fontWeight: 700, fontSize: "0.9rem" }}
      >
        {`${Math.round(value)}%`}
      </Typography>
    </Box>
  </Box>
);

/**
 * Custom Filter Panel Component
 *
 * @param {Object} props
 * @param {Array} props.filterConfig - Array of filter field configurations
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Object} props.initialFilters - Initial filter values
 * @param {Boolean} props.collapsible - Whether the panel can be collapsed
 * @param {String} props.title - Title for the filter panel
 * @param {Number} props.resultCount - Number of results matching current filters
 * @param {Number} props.totalCount - Total number of records (before filtering)
 * @param {Function} props.onExpand - Optional callback fired the first time the panel is expanded (useful for lazy-loading filter data)
 * @param {Boolean} props.isSearching - Shows a green circular progress (with % label) next to the results chip while a filter/search pass is computing (e.g. useCustomFilter's isFiltering)
 */
const CustomFilterPanel = ({
  filterConfig = [],
  onFiltersChange,
  initialFilters = {},
  collapsible = true,
  title = "Filters",
  resultCount,
  totalCount,
  onExpand,
  isSearching = false,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [searchProgress, setSearchProgress] = useState(100);

  // Step the search progress circle up in 10% increments while a filter
  // pass is computing (capped at 90% so it never fakes completion), then
  // snap straight to 100% the moment isSearching actually clears.
  useEffect(() => {
    if (!isSearching) {
      setSearchProgress(100);
      return undefined;
    }

    setSearchProgress(0);
    const interval = setInterval(() => {
      setSearchProgress((prev) => (prev >= 90 ? 90 : prev + 10));
    }, 120);

    return () => clearInterval(interval);
  }, [isSearching]);

  // Check if there are any active filters
  useEffect(() => {
    const activeCount = Object.values(filters).filter((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object" && value !== null)
        return Object.keys(value).length > 0;
      return value !== null && value !== undefined && value !== "";
    }).length;
    setHasActiveFilters(activeCount > 0);
  }, [filters]);

  // Update filters when they change
  const handleFilterChange = (fieldName, value) => {
    const newFilters = { ...filters, [fieldName]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  // Clear all filters
  const handleClearAll = () => {
    const clearedFilters = {};
    filterConfig.forEach((field) => {
      switch (field.type) {
        case "multiselect":
          clearedFilters[field.name] = [];
          break;
        case "daterange":
          clearedFilters[field.name] = { start: null, end: null };
          break;
        default:
          clearedFilters[field.name] = "";
      }
    });
    setFilters(clearedFilters);
    onFiltersChange?.(clearedFilters);
  };

  // Render individual filter field
  const renderFilterField = (field) => {
    const value = filters[field.name] || field.defaultValue || "";

    switch (field.type) {
      case "text":
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFilterChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            size="small"
          />
        );

      case "select":
        return (
          <FormControl key={field.name} fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleFilterChange(field.name, e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "multiselect":
        return (
          <Autocomplete
            key={field.name}
            multiple
            fullWidth
            size="small"
            options={field.options || []}
            getOptionLabel={(option) => option.label || option}
            value={Array.isArray(value) ? value : []}
            onChange={(event, newValue) =>
              handleFilterChange(field.name, newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                placeholder={field.placeholder}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.label || option}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        );

      case "boolean":
        return (
          <FormControl key={field.name} fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleFilterChange(field.name, e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {field.options
                ? field.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))
                : [
                    <MenuItem key="Yes" value="Yes">
                      Yes
                    </MenuItem>,
                    <MenuItem key="No" value="No">
                      No
                    </MenuItem>,
                  ]}
            </Select>
          </FormControl>
        );

      case "switch":
        return (
          <FormControlLabel
            key={field.name}
            control={
              <Switch
                checked={value === true}
                onChange={(e) =>
                  handleFilterChange(field.name, e.target.checked)
                }
              />
            }
            label={field.label}
          />
        );

      case "date":
        return (
          <LocalizationProvider key={field.name} dateAdapter={AdapterDayjs}>
            <DatePicker
              label={field.label}
              value={value ? dayjs(value) : null}
              onChange={(newValue) =>
                handleFilterChange(
                  field.name,
                  newValue ? newValue.toDate() : null,
                )
              }
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        );

      case "daterange":
        const dateRangeValue = value || { start: null, end: null };
        return (
          <LocalizationProvider key={field.name} dateAdapter={AdapterDayjs}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {field.label}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <DatePicker
                    label="From"
                    value={
                      dateRangeValue.start ? dayjs(dateRangeValue.start) : null
                    }
                    onChange={(newValue) =>
                      handleFilterChange(field.name, {
                        ...dateRangeValue,
                        start: newValue ? newValue.toDate() : null,
                      })
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="To"
                    value={
                      dateRangeValue.end ? dayjs(dateRangeValue.end) : null
                    }
                    onChange={(newValue) =>
                      handleFilterChange(field.name, {
                        ...dateRangeValue,
                        end: newValue ? newValue.toDate() : null,
                      })
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </LocalizationProvider>
        );

      case "number":
        return (
          <TextField
            key={field.name}
            fullWidth
            type="number"
            label={field.label}
            value={value}
            onChange={(e) => handleFilterChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            size="small"
            inputProps={{
              min: field.min,
              max: field.max,
              step: field.step,
            }}
          />
        );

      case "numberrange":
        const numberRangeValue = value || { min: "", max: "" };
        return (
          <Box key={field.name}>
            <Typography variant="subtitle2" gutterBottom>
              {field.label}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min"
                  value={numberRangeValue.min}
                  onChange={(e) =>
                    handleFilterChange(field.name, {
                      ...numberRangeValue,
                      min: e.target.value,
                    })
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max"
                  value={numberRangeValue.max}
                  onChange={(e) =>
                    handleFilterChange(field.name, {
                      ...numberRangeValue,
                      max: e.target.value,
                    })
                  }
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  // Count active filters for display
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object" && value !== null) {
        if (value.start || value.end) return true;
        if (value.min !== "" || value.max !== "") return true;
        return Object.keys(value).length > 0;
      }
      return value !== null && value !== undefined && value !== "";
    }).length;
  }, [filters]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: collapsible && !isExpanded ? 1 : 2 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: collapsible && !isExpanded ? 0 : 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FontAwesomeIcon icon={faFilter} />
            <Typography variant="h6">{title}</Typography>
            {activeFilterCount > 0 && (
              <Chip
                label={`${activeFilterCount} active`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {resultCount !== undefined && totalCount !== undefined && (
              <Chip
                label={
                  activeFilterCount > 0
                    ? `${resultCount} of ${totalCount} results`
                    : `${totalCount} total records`
                }
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            {resultCount !== undefined && totalCount !== undefined && (
              <SearchProgressCircle value={searchProgress} />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<FontAwesomeIcon icon={faTimes} />}
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            )}
            {collapsible && (
              <IconButton
                size="small"
                onClick={() => {
                  const next = !isExpanded;
                  setIsExpanded(next);
                  if (next && onExpand) onExpand();
                }}
              >
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronUp : faChevronDown}
                />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Filter Fields */}
        <Collapse in={!collapsible || isExpanded}>
          <Grid container spacing={2}>
            {filterConfig.map((field, index) => (
              <Grid
                item
                xs={12}
                sm={field.gridSize?.sm || 6}
                md={field.gridSize?.md || 4}
                lg={field.gridSize?.lg || 3}
                key={`${field.name}-${index}`}
              >
                {renderFilterField(field)}
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default CustomFilterPanel;
